import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import BookingRejectionDialog from '../../components/BookingRejectionDialog';
import HostTutorBookingProposeChangeDialog from '../../components/HostTutorBookingProposeChangeDialog';
import TutorBookingRequestCard from '../../components/tutor/TutorBookingRequestCard';
import TutorSeriesRequestCard from '../../components/tutor/TutorSeriesRequestCard';
import TutorBookingRequestsPageShell from '../../components/tutor/TutorBookingRequestsPageShell';
import TutorBookingFilterTabs from '../../components/tutor/TutorBookingFilterTabs';
import TutorBookingStudentSearchField from '../../components/tutor/TutorBookingStudentSearchField';
import { hasPendingTutorChangeProposal } from '../../components/TutorBookingRequestTableCells';
import { hostApiClient } from '../../hostApiClient';
import { Room } from '../../models/Room';
import { HostTutorBookingResponse, TutorBookingFilter, TutorBookingSummaryCountsResponse } from '../../models/Tutor';
import {
  canCancelConfirmedTutorBooking,
  canHostCancelTutorBookingChangeProposal,
  canHostProposeTutorBookingChange,
  canShowTutorBookingRoomSection,
  isPastTutorBookingStart,
  isTutorBookingModeratable,
  tutorBookingAcceptDisabled,
  tutorBookingDeclineDisabled,
} from '../../utils/tutorBookingStatus';
import { canRefundTutorBooking } from '../../utils/tutorBookingPayment';
import { canCollectTutorBookingPayment } from '../../utils/bookingPaymentCollection';
import {
  extractTutorBookingsPageTotal,
  HOST_TUTOR_BOOKING_FILTERS,
} from '../../utils/tutorBookingFilters';
import { notifyTutorBookingNeedsActionCountChanged } from '../../utils/tutorBookingNeedsActionCount';
import { extractErrorMessage } from '../../utils/errorHandler';
import { tutorBookingMatchesStudentNameSearch } from '../../utils/tutorBookingStudentName';
import {
  groupHostTutorBookingRequests,
  pendingOnlineTutorUsesSeriesEndpoint,
} from '../../utils/hostTutorBookingGrouping';
import { tutorBookingDetailsPath } from '../../utils/tutorBookingDetailsPath';

type HostTutorNegativeDialogMode = 'decline' | 'cancel';

const HostTutorBookingRequestsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<TutorBookingFilter>('needs_tutor_action');
  const [summary, setSummary] = useState<TutorBookingSummaryCountsResponse | null>(null);
  const [bookings, setBookings] = useState<HostTutorBookingResponse[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [negativeDialogMode, setNegativeDialogMode] =
    useState<HostTutorNegativeDialogMode>('decline');
  const [proposeDialogOpen, setProposeDialogOpen] = useState(false);
  const [proposeBooking, setProposeBooking] = useState<HostTutorBookingResponse | null>(null);
  const [proposingChange, setProposingChange] = useState(false);
  const [cancelChangeDialogOpen, setCancelChangeDialogOpen] = useState(false);
  const [cancelChangeReason, setCancelChangeReason] = useState('');
  const [cancelChangeTarget, setCancelChangeTarget] = useState<HostTutorBookingResponse | null>(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundTarget, setRefundTarget] = useState<HostTutorBookingResponse | null>(null);
  const [refundingId, setRefundingId] = useState<string | null>(null);
  const [collectPaymentDialogOpen, setCollectPaymentDialogOpen] = useState(false);
  const [collectPaymentTarget, setCollectPaymentTarget] = useState<HostTutorBookingResponse | null>(null);
  const [collectingPaymentId, setCollectingPaymentId] = useState<string | null>(null);
  const [roomsBySpaceId, setRoomsBySpaceId] = useState<Record<string, Room[]>>({});
  const [loadingSpaceIds, setLoadingSpaceIds] = useState<Record<string, boolean>>({});
  const [studentSearch, setStudentSearch] = useState('');

  const filteredBookings = useMemo(
    () =>
      bookings.filter((booking) =>
        tutorBookingMatchesStudentNameSearch(
          { studentProfile: booking.studentProfile, userName: booking.userName },
          studentSearch,
        ),
      ),
    [bookings, studentSearch],
  );

  const roomSpaceIds = useMemo(() => {
    const ids = new Set<string>();
    bookings.forEach((booking) => {
      if (
        canShowTutorBookingRoomSection({
          spaceId: booking.spaceId,
          lessonSessionType: booking.lessonSessionType,
        })
      ) {
        ids.add(booking.spaceId as string);
      }
    });
    return Array.from(ids).sort();
  }, [bookings]);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await hostApiClient.getHostTutorBookingsSummary();
      setSummary(data);
    } catch {
      // Non-blocking; tab counts stay at 0 until next successful fetch.
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await hostApiClient.getHostTutorBookings(page, rowsPerPage, activeFilter);
      setBookings(response.content);
      setTotalElements(extractTutorBookingsPageTotal(response));
    } catch (_e) {
      setError(t('hostTutorBookings.fetchError', 'Failed to load tutor bookings'));
    } finally {
      setLoading(false);
    }
  }, [activeFilter, page, rowsPerPage, t]);

  const refreshAfterMutation = useCallback(async () => {
    await Promise.all([fetchBookings(), fetchSummary()]);
    notifyTutorBookingNeedsActionCountChanged();
  }, [fetchBookings, fetchSummary]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    setPage(0);
  }, [activeFilter]);

  useEffect(() => {
    let cancelled = false;

    const loadRooms = async () => {
      const missingSpaceIds = roomSpaceIds.filter((spaceId) => roomsBySpaceId[spaceId] == null);
      if (missingSpaceIds.length === 0) return;

      setLoadingSpaceIds((prev) => {
        const next = { ...prev };
        missingSpaceIds.forEach((spaceId) => {
          next[spaceId] = true;
        });
        return next;
      });

      await Promise.all(
        missingSpaceIds.map(async (spaceId) => {
          try {
            const rooms = await hostApiClient.getRooms(spaceId);
            if (!cancelled) {
              setRoomsBySpaceId((prev) => ({ ...prev, [spaceId]: rooms }));
            }
          } catch {
            if (!cancelled) {
              setRoomsBySpaceId((prev) => ({ ...prev, [spaceId]: [] }));
            }
          } finally {
            if (!cancelled) {
              setLoadingSpaceIds((prev) => {
                const next = { ...prev };
                delete next[spaceId];
                return next;
              });
            }
          }
        }),
      );
    };

    loadRooms();
    return () => {
      cancelled = true;
    };
    // roomsBySpaceId intentionally omitted: only fetch missing space ids once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomSpaceIds]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatSeriesWhen = (startTime: string, endTime: string) => {
    const start = new Date(startTime.endsWith('Z') ? startTime : `${startTime}Z`);
    const end = new Date(endTime.endsWith('Z') ? endTime : `${endTime}Z`);
    const date = start.toLocaleString(i18n.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const endLabel = end.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
    return `${date}–${endLabel}`;
  };

  const formatSeriesAmount = (amount: number, currencyCode: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currencyCode || 'PLN',
    }).format(amount);

  const handleAccept = async (bookingId: string) => {
    setProcessingId(bookingId);
    setError(null);
    try {
      const row = bookings.find((booking) => booking.bookingId === bookingId);
      if (row && pendingOnlineTutorUsesSeriesEndpoint(row)) {
        await hostApiClient.acceptTutorBookingSeries(row.recurringSeriesId!.trim());
      } else {
        await hostApiClient.acceptTutorBooking(bookingId);
      }
      await refreshAfterMutation();
      setActiveFilter('upcoming');
    } catch {
      setError(t('hostTutorBookings.acceptError', 'Failed to accept tutor booking'));
    } finally {
      setProcessingId(null);
    }
  };

  const openDeclineDialog = (bookingId: string) => {
    setSelectedBookingId(bookingId);
    setDeclineReason('');
    setNegativeDialogMode('decline');
    setDeclineDialogOpen(true);
  };

  const openCancelLessonDialog = (row: HostTutorBookingResponse) => {
    if (!canCancelConfirmedTutorBooking(row.status, row.startTime)) return;
    setSelectedBookingId(row.bookingId);
    setDeclineReason('');
    setNegativeDialogMode('cancel');
    setDeclineDialogOpen(true);
  };

  const closeNegativeDialog = () => {
    if (processingId === selectedBookingId) return;
    setDeclineDialogOpen(false);
    setDeclineReason('');
    setSelectedBookingId(null);
    setNegativeDialogMode('decline');
  };

  const handleDeclineOrCancelLesson = async () => {
    if (!selectedBookingId) return;
    const reason = declineReason.trim();
    if (!reason) return;
    const isCancel = negativeDialogMode === 'cancel';
    setProcessingId(selectedBookingId);
    try {
      const row = bookings.find((booking) => booking.bookingId === selectedBookingId);
      if (!isCancel && row && pendingOnlineTutorUsesSeriesEndpoint(row)) {
        await hostApiClient.declineTutorBookingSeries(row.recurringSeriesId!.trim(), reason);
      } else {
        await hostApiClient.declineTutorBooking(selectedBookingId, reason);
      }
      setDeclineDialogOpen(false);
      setDeclineReason('');
      setSelectedBookingId(null);
      setNegativeDialogMode('decline');
      await refreshAfterMutation();
      if (isCancel) {
        setActiveFilter('cancelled');
      }
    } catch {
      setError(
        isCancel
          ? t('hostTutorBookings.cancelLessonError', 'Failed to cancel tutor lesson')
          : t('hostTutorBookings.declineError', 'Failed to decline tutor booking'),
      );
    } finally {
      setProcessingId(null);
    }
  };

  const openProposeChangeDialog = (row: HostTutorBookingResponse) => {
    setProposeBooking(row);
    setProposeDialogOpen(true);
  };

  const handleProposeChangeSubmit = async (
    newStartTime: string,
    reason: string,
    autoAcceptTutorBooking: boolean,
  ) => {
    if (!proposeBooking) return;
    setProposingChange(true);
    setError(null);
    try {
      await hostApiClient.proposeTutorBookingChange(proposeBooking.bookingId, {
        newStartTime,
        reason: reason || undefined,
        autoAcceptTutorBooking,
      });
      setProposeDialogOpen(false);
      setProposeBooking(null);
      await refreshAfterMutation();
    } catch {
      setError(t('hostTutorBookings.proposeChangeError', 'Failed to propose schedule change'));
    } finally {
      setProposingChange(false);
    }
  };

  const openCancelChangeDialog = (row: HostTutorBookingResponse) => {
    setCancelChangeTarget(row);
    setCancelChangeReason('');
    setCancelChangeDialogOpen(true);
  };

  const handleCancelChangeProposal = async () => {
    if (!cancelChangeTarget?.changeProposal) return;
    setProcessingId(cancelChangeTarget.bookingId);
    setError(null);
    try {
      await hostApiClient.cancelTutorBookingChange(
        cancelChangeTarget.bookingId,
        cancelChangeTarget.changeProposal.id,
        { reason: cancelChangeReason.trim() || undefined },
      );
      setCancelChangeDialogOpen(false);
      setCancelChangeTarget(null);
      setCancelChangeReason('');
      await refreshAfterMutation();
    } catch {
      setError(t('hostTutorBookings.cancelChangeError', 'Failed to cancel change proposal'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleAssignRoom = async (bookingId: string, roomId: string) => {
    setProcessingId(bookingId);
    setError(null);
    try {
      await hostApiClient.assignTutorBookingRoom(bookingId, { roomId });
      await refreshAfterMutation();
    } catch (err) {
      setError(
        extractErrorMessage(err) ||
          t('hostTutorBookings.assignRoomError', 'Failed to assign room for this lesson'),
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleClearRoom = async (bookingId: string) => {
    setProcessingId(bookingId);
    setError(null);
    try {
      await hostApiClient.clearTutorBookingRoom(bookingId);
      await refreshAfterMutation();
    } catch (err) {
      setError(
        extractErrorMessage(err) ||
          t('hostTutorBookings.clearRoomError', 'Failed to clear room assignment'),
      );
    } finally {
      setProcessingId(null);
    }
  };

  const showModerationActions = (row: HostTutorBookingResponse) =>
    !isPastTutorBookingStart(row.startTime) &&
    isTutorBookingModeratable(row.status) &&
    !hasPendingTutorChangeProposal(row.changeProposal);

  const openRefundDialog = (row: HostTutorBookingResponse) => {
    if (!canRefundTutorBooking(row)) return;
    setRefundTarget(row);
    setRefundReason('');
    setRefundDialogOpen(true);
  };

  const closeRefundDialog = () => {
    if (refundingId) return;
    setRefundDialogOpen(false);
    setRefundReason('');
    setRefundTarget(null);
  };

  const handleRefundSubmit = async () => {
    if (!refundTarget || !canRefundTutorBooking(refundTarget)) return;
    setRefundingId(refundTarget.bookingId);
    setError(null);
    try {
      const trimmed = refundReason.trim();
      await hostApiClient.refundTutorBooking(
        refundTarget.bookingId,
        trimmed ? { refund_reason: trimmed } : {},
      );
      setRefundDialogOpen(false);
      setRefundReason('');
      setRefundTarget(null);
      await refreshAfterMutation();
    } catch {
      setError(t('hostTutorBookings.refundError', 'Failed to issue refund'));
    } finally {
      setRefundingId(null);
    }
  };

  const openCollectPaymentDialog = (row: HostTutorBookingResponse) => {
    if (!canCollectTutorBookingPayment(row)) return;
    setCollectPaymentTarget(row);
    setCollectPaymentDialogOpen(true);
  };

  const closeCollectPaymentDialog = () => {
    if (collectingPaymentId) return;
    setCollectPaymentDialogOpen(false);
    setCollectPaymentTarget(null);
  };

  const handleCollectPaymentSubmit = async () => {
    if (!collectPaymentTarget || !canCollectTutorBookingPayment(collectPaymentTarget)) return;
    setCollectingPaymentId(collectPaymentTarget.bookingId);
    setError(null);
    try {
      await hostApiClient.collectTutorBookingPayment(collectPaymentTarget.bookingId);
      setCollectPaymentDialogOpen(false);
      setCollectPaymentTarget(null);
      await refreshAfterMutation();
    } catch {
      setError(t('hostTutorBookings.collectPaymentError', 'Failed to mark payment as collected'));
    } finally {
      setCollectingPaymentId(null);
    }
  };

  const pendingActionCount = summary?.needsTutorAction ?? 0;

  const emptyFilterKey =
    activeFilter === 'needs_user_action' ? 'awaiting_user' : activeFilter;

  const emptyDescriptionForFilter = t(`tutorBookingFilters.empty.${emptyFilterKey}`, {
    defaultValue: t(
      'hostTutorBookings.emptyDescription',
      'Tutor lesson requests from students will show up here once submitted.',
    ),
  });

  return (
    <>
      <TutorBookingRequestsPageShell
        title={t('hostTutorBookings.title', 'Tutor booking requests')}
        subtitle={t(
          'hostTutorBookings.subtitle',
          'Manage tutor lesson bookings for your organization — confirm requests and handle schedule changes.',
        )}
        pendingActionCount={pendingActionCount}
        loading={loading}
        hasLoadedOnce={bookings.length > 0 || !loading}
        error={error}
        onRetry={() => fetchBookings()}
        onDismissError={() => setError(null)}
        emptyTitle={t(`tutorBookingFilters.empty.${emptyFilterKey}`, {
          defaultValue: t('hostTutorBookings.emptyTitle', 'No tutor bookings yet'),
        })}
        emptyDescription={emptyDescriptionForFilter}
        isEmpty={bookings.length === 0 && !loading}
      >
        <TutorBookingFilterTabs
          filters={HOST_TUTOR_BOOKING_FILTERS}
          activeFilter={activeFilter}
          summary={summary}
          onChange={(filter) => {
            setActiveFilter(filter);
            setStudentSearch('');
          }}
          labelOverrides={{
            needs_user_action: t('tutorBookingFilters.awaiting_user', 'Waiting for the student'),
          }}
          trailing={
            bookings.length > 0 ? (
              <TutorBookingStudentSearchField
                value={studentSearch}
                onChange={setStudentSearch}
                disabled={loading}
              />
            ) : undefined
          }
        />
        {bookings.length > 0 ? (
          <>
            {filteredBookings.length > 0 ? (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {groupHostTutorBookingRequests(filteredBookings).map((entry) => {
                  if (entry.kind === 'series') {
                    return (
                      <Grid item xs={12} key={entry.seriesId}>
                        <TutorSeriesRequestCard
                          seriesId={entry.seriesId}
                          bookings={entry.bookings}
                          busy={processingId === entry.seriesId}
                          formatWhen={formatSeriesWhen}
                          formatAmount={formatSeriesAmount}
                          onAcceptSeries={(seriesId) => hostApiClient.acceptTutorBookingSeries(seriesId)}
                          onAcceptSelected={(seriesId, bookingIds, declineReason) =>
                            hostApiClient.acceptSelectedTutorBookingSeries(seriesId, bookingIds, declineReason)
                          }
                          onAcceptOne={(bookingId) => hostApiClient.acceptTutorBooking(bookingId)}
                          onDeclineSeries={(seriesId, reason) =>
                            hostApiClient.declineTutorBookingSeries(seriesId, reason)
                          }
                          onOpen={(booking) => navigate(tutorBookingDetailsPath('host', booking.bookingId))}
                          onChanged={refreshAfterMutation}
                          onError={setError}
                        />
                      </Grid>
                    );
                  }
                  const row = entry.booking;
                  const busy = processingId === row.bookingId || proposingChange;
                  const spaceId = row.spaceId || '';
                  return (
                    <Grid item xs={12} md={6} key={row.bookingId}>
                      <TutorBookingRequestCard
                        row={row}
                        perspective="host"
                        busy={busy}
                        processingBookingId={processingId}
                        showModeration={showModerationActions(row)}
                        canPropose={canHostProposeTutorBookingChange(row.status, row.startTime, row.changeProposal)}
                        canCancelProposal={canHostCancelTutorBookingChangeProposal(row.changeProposal)}
                        canCancelLesson={canCancelConfirmedTutorBooking(row.status, row.startTime)}
                        acceptDisabled={tutorBookingAcceptDisabled(row.status, row.startTime)}
                        declineDisabled={tutorBookingDeclineDisabled(row.status, row.startTime)}
                        onAccept={handleAccept}
                        onDecline={openDeclineDialog}
                        onPropose={openProposeChangeDialog}
                        onCancelProposal={openCancelChangeDialog}
                        onCancelLesson={openCancelLessonDialog}
                        canRefund={canRefundTutorBooking(row)}
                        onRefund={openRefundDialog}
                        refundLoading={refundingId === row.bookingId}
                        canCollectPayment={canCollectTutorBookingPayment(row)}
                        onCollectPayment={openCollectPaymentDialog}
                        collectPaymentLoading={collectingPaymentId === row.bookingId}
                        spaceRooms={spaceId ? roomsBySpaceId[spaceId] || [] : []}
                        spaceRoomsLoading={!!(spaceId && loadingSpaceIds[spaceId])}
                        onAssignRoom={handleAssignRoom}
                        onClearRoom={handleClearRoom}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                {t(
                  'tutorBookingRequests.noStudentMatches',
                  'No bookings on this page match that student name.',
                )}
              </Alert>
            )}
            <TablePagination
              component="div"
              count={totalElements}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={isMobile ? [] : [5, 10, 25]}
              labelRowsPerPage={t('common.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                t('common.pagination.displayedRows', { from, to, count })
              }
              sx={{ mt: 1 }}
            />
          </>
        ) : null}
      </TutorBookingRequestsPageShell>
      <HostTutorBookingProposeChangeDialog
        open={proposeDialogOpen}
        booking={proposeBooking}
        submitting={proposingChange}
        onClose={() => {
          if (!proposingChange) {
            setProposeDialogOpen(false);
            setProposeBooking(null);
          }
        }}
        onSubmit={handleProposeChangeSubmit}
      />

      <Dialog
        open={cancelChangeDialogOpen}
        onClose={() => !processingId && setCancelChangeDialogOpen(false)}
      >
        <DialogTitle>{t('bookings.changeProposal.cancel', 'Cancel proposal')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t(
              'hostTutorBookings.changeProposal.cancelMessage',
              'Withdraw this schedule change before the student responds.',
            )}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            margin="dense"
            label={t('hostTutorBookings.changeProposal.cancelReasonOptional', 'Note (optional)')}
            value={cancelChangeReason}
            onChange={(e) => setCancelChangeReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelChangeDialogOpen(false)} disabled={!!processingId}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCancelChangeProposal}
            disabled={!!processingId}
            startIcon={processingId ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('bookings.changeProposal.confirmCancel', 'Cancel proposal')}
          </Button>
        </DialogActions>
      </Dialog>

      <BookingRejectionDialog
        open={declineDialogOpen}
        title={
          negativeDialogMode === 'decline'
            ? t('roomBookingRequests.declineTitle')
            : t('hostTutorBookings.cancelLessonTitle', 'Cancel confirmed lesson')
        }
        description={
          negativeDialogMode === 'decline'
            ? t(
                'roomBookingRequests.declineDescription',
                'Declining closes this request. The person booking will be notified and will see the reason you provide.',
              )
            : t(
                'hostTutorBookings.cancelLessonDescription',
                'You have already confirmed this lesson. Are you sure you want to cancel it? The student will be notified and will see the reason you provide.',
              )
        }
        reason={declineReason}
        reasonLabel={
          negativeDialogMode === 'decline'
            ? t('roomBookingRequests.declineReason')
            : t('hostTutorBookings.cancelLessonReason', 'Cancellation reason')
        }
        reasonPlaceholder={t(
          negativeDialogMode === 'decline'
            ? 'roomBookingRequests.declineReasonPlaceholder'
            : 'hostTutorBookings.cancelLessonReasonPlaceholder',
          negativeDialogMode === 'decline'
            ? 'Briefly explain why you cannot accept this request'
            : 'Briefly explain why you need to cancel this lesson',
        )}
        reasonHelperText={t(
          negativeDialogMode === 'decline'
            ? 'roomBookingRequests.declineReasonHelper'
            : 'hostTutorBookings.cancelLessonReasonHelper',
          'A reason is required and will be shared with the student.',
        )}
        confirmLabel={
          negativeDialogMode === 'decline'
            ? t('roomBookingRequests.decline')
            : t('hostTutorBookings.cancelLessonButton', 'Cancel lesson')
        }
        cancelLabel={
          negativeDialogMode === 'decline'
            ? t('common.cancel', 'Cancel')
            : t('hostTutorBookings.keepLesson', 'Keep lesson')
        }
        closeLabel={t('common.close', 'Close')}
        loading={processingId === selectedBookingId}
        onReasonChange={setDeclineReason}
        onClose={closeNegativeDialog}
        onConfirm={handleDeclineOrCancelLesson}
        confirmColor="error"
        fullScreen={isMobile}
        notice={
          negativeDialogMode === 'decline' &&
          bookings.some(
            (booking) =>
              booking.bookingId === selectedBookingId && pendingOnlineTutorUsesSeriesEndpoint(booking),
          ) ? (
            <Typography variant="body2">
              {t('bookings.declineSeriesWarning', 'This will affect all sessions in this series.')}
            </Typography>
          ) : undefined
        }
      />

      <Dialog open={refundDialogOpen} onClose={closeRefundDialog}>
        <DialogTitle>{t('hostTutorBookings.refundDialogTitle', 'Issue refund')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t(
              'hostTutorBookings.refundDialogDescription',
              'Refund or release payment for this cancelled tutor booking. The backend applies Stripe refund or payment-intent cancellation rules.',
            )}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            margin="dense"
            label={t('hostTutorBookings.refundReasonLabel', 'Refund note (optional)')}
            placeholder={t('hostTutorBookings.refundReasonPlaceholder', 'e.g. duplicate charge')}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRefundDialog} disabled={!!refundingId}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRefundSubmit}
            disabled={!!refundingId}
            startIcon={refundingId ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('hostTutorBookings.issueRefund', 'Issue refund')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={collectPaymentDialogOpen} onClose={closeCollectPaymentDialog}>
        <DialogTitle>{t('hostTutorBookings.collectPaymentDialogTitle', 'Mark as paid')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'hostTutorBookings.collectPaymentDialogDescription',
              'Confirm that on-site payment was collected for this lesson. The booking will be marked paid and the collection time will be recorded.',
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCollectPaymentDialog} disabled={!!collectingPaymentId}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCollectPaymentSubmit}
            disabled={!!collectingPaymentId}
            startIcon={
              collectingPaymentId ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {t('hostTutorBookings.collectPayment', 'Mark as paid')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HostTutorBookingRequestsPage;
